import type maplibregl from "maplibre-gl";

/**
 * Keeps the screen on while the crew is actively navigating. The lock is
 * dropped by the browser whenever the page is hidden, so it has to be
 * re-acquired on every return to the foreground.
 */
class ScreenAwake {
  private sentinel: WakeLockSentinel | null = null;
  private wanted = false;

  constructor() {
    document.addEventListener("visibilitychange", () => {
      if (this.wanted && document.visibilityState === "visible") void this.acquire();
    });
  }

  supported(): boolean {
    return "wakeLock" in navigator;
  }

  async enable(): Promise<void> {
    this.wanted = true;
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.wanted = false;
    try {
      await this.sentinel?.release();
    } catch {
      // Releasing an already-released lock is not an error worth surfacing.
    }
    this.sentinel = null;
  }

  private async acquire(): Promise<void> {
    if (!this.supported() || this.sentinel) return;
    try {
      this.sentinel = await navigator.wakeLock.request("screen");
      this.sentinel.addEventListener("release", () => {
        this.sentinel = null;
      });
    } catch {
      // Denied (low battery, unsupported); tracking still works.
    }
  }
}

export interface TrackingStatus {
  state: "off" | "searching" | "tracking" | "denied" | "error";
  message?: string;
  accuracy?: number;
  longitude?: number;
  latitude?: number;
  /** Milliseconds since Unix epoch, supplied by the Geolocation API. */
  timestamp?: number;
  /** Raw device speed in metres per second. Undefined when the device omits it. */
  speedMetresPerSecond?: number;
  /** Direction of travel in true degrees. Undefined when the device omits it. */
  courseDegrees?: number;
}

export interface TrackingHandle {
  start: () => void;
  stop: () => Promise<void>;
}

/**
 * Wraps MapLibre's GeolocateControl so the rest of the app sees one plain
 * status stream, and so the screen stays awake only while a fix is being
 * followed. GPS itself needs no network, which is why position tracking keeps
 * working offshore.
 */
export function attachTracking(
  geolocate: maplibregl.GeolocateControl,
  onStatus: (status: TrackingStatus) => void,
): TrackingHandle {
  const awake = new ScreenAwake();

  geolocate.on("trackuserlocationstart", () => {
    onStatus({ state: "searching" });
    void awake.enable();
  });

  geolocate.on("trackuserlocationend", () => {
    onStatus({ state: "off" });
    void awake.disable();
  });

  geolocate.on("geolocate", (event: unknown) => {
    const position = event as GeolocationPosition;
    onStatus({
      state: "tracking",
      accuracy: position.coords.accuracy,
      longitude: position.coords.longitude,
      latitude: position.coords.latitude,
      timestamp: position.timestamp,
      speedMetresPerSecond:
        position.coords.speed !== null && Number.isFinite(position.coords.speed)
          ? Math.max(0, position.coords.speed)
          : undefined,
      courseDegrees:
        position.coords.heading !== null &&
        Number.isFinite(position.coords.heading) &&
        position.coords.heading >= 0
          ? position.coords.heading % 360
          : undefined,
    });
  });

  geolocate.on("error", (event: unknown) => {
    const error = event as GeolocationPositionError;
    if (error.code === 1) {
      onStatus({
        state: "denied",
        message:
          "Location permission is off. Enable it for this app in your browser settings to see your position.",
      });
    } else {
      onStatus({
        state: "error",
        message:
          error.code === 3
            ? "No GPS fix yet. Under way with a clear view of the sky this usually settles within a minute."
            : "Position unavailable.",
      });
    }
    void awake.disable();
  });

  return {
    start: () => geolocate.trigger(),
    stop: () => awake.disable(),
  };
}
